import { AppShellContent, Button, Headline, Icon, Text } from "@assetwatch/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  type AssetSortField,
  type AssetTreeSearch,
  resolveAssetSort,
} from "../../../lib/assetTreeSearch";
import { canStartWorkOrderFromAsset } from "../../../lib/assetWorkOrders";
import type { AssetId, WorkOrderId } from "../../../lib/workIds";
import { getWorkScopeSearch } from "../../../lib/workSearch";
import { useAssetTreeQuery } from "../../../queries/useAssetTreeQuery";
import { useAssetWorkOrderCreation } from "../../../queries/useAssetWorkOrderCreation";
import { AssetWorkOrderModal } from "../../AssetWorkOrderModal";
import {
  WorkPage,
  WorkPageState,
  WorkPanel,
} from "../../layout/WorkPageLayout";
import { useOptionalWorkScope } from "../../WorkScopeProvider";
import { SearchFilterToolbar } from "../SearchFilterToolbar";
import { AssetBreadcrumbs } from "./AssetBreadcrumbs";
import { AssetDetailSideSheet } from "./AssetDetailSideSheet";
import { AssetKindFilter } from "./AssetKindFilter";
import { AssetsTable } from "./AssetsTable";
import {
  type AssetViewRow,
  findPhysicalLocation,
  flattenAssetTree,
  orderAssetRows,
  sortAssetRows,
} from "./assetModel";

type KindFilter = "all" | "ASSET" | "LOCATION";

export function AssetsWorkspace({
  routeSearch,
  onRouteSearchChange,
}: {
  routeSearch?: AssetTreeSearch;
  onRouteSearchChange?: (update: Partial<AssetTreeSearch>) => void;
}) {
  const [localRouteSearch, setLocalRouteSearch] = useState<AssetTreeSearch>({});
  const currentRouteSearch = routeSearch ?? localRouteSearch;
  const scope = useOptionalWorkScope();
  const selectedCustomer = scope?.selectedCustomer;
  const selectedFacility = scope?.selectedFacility;
  const navigate = useNavigate();
  const { createWorkOrder, attachProcedure } = useAssetWorkOrderCreation();
  const [isNewWorkOrderOpen, setIsNewWorkOrderOpen] = useState(false);
  const assetTreeQuery = useAssetTreeQuery();
  const [search, setSearch] = useState(currentRouteSearch.assetSearch ?? "");
  const [kindFilter, setKindFilter] = useState<KindFilter>(
    currentRouteSearch.assetKind ?? "all",
  );
  const [transientSelectedId, setTransientSelectedId] = useState<number | null>(
    null,
  );
  const baseRows = flattenAssetTree(assetTreeQuery.data?.nodes ?? []);
  const allRows = orderAssetRows(baseRows);
  // The facility/Site can have multiple root-level siblings (several
  // top-level Locations with no parent), since the Site itself is never a
  // synthetic Work.Asset row. `selected` stays `null` at the true root
  // ("All Assets") so `tableRows` below naturally shows every root row,
  // not just the first one found.
  const selectedId = currentRouteSearch.assetSelectedId
    ? (allRows.find((row) => row.assetId === currentRouteSearch.assetSelectedId)
        ?.id ?? transientSelectedId)
    : transientSelectedId;
  const selected = allRows.find((row) => row.id === selectedId) ?? null;
  const effectiveSelectedId = selected?.id ?? null;
  const openAssetId = currentRouteSearch.assetOpenId ?? null;
  const openRow = openAssetId
    ? (allRows.find((row) => row.assetId === openAssetId) ?? null)
    : null;
  const physicalLocationRow = openRow
    ? findPhysicalLocation(allRows, openRow)
    : null;
  const openRowSubAssets = openRow
    ? allRows
        .filter(
          (row): row is typeof row & { assetId: AssetId } =>
            row.parentId === openRow.id && row.assetId !== null,
        )
        .map((row) => ({
          assetId: row.assetId,
          code: row.code,
          name: row.name,
        }))
    : [];
  const canCreateWorkOrder =
    Boolean(openRow?.assetId) &&
    canStartWorkOrderFromAsset(openRow?.kind, selectedFacility ?? null);

  useEffect(() => {
    setSearch(currentRouteSearch.assetSearch ?? "");
    setKindFilter(currentRouteSearch.assetKind ?? "all");
  }, [currentRouteSearch.assetSearch, currentRouteSearch.assetKind]);
  useEffect(() => {
    if (routeSearch) {
      setTransientSelectedId(null);
    }
  }, [routeSearch]);

  function updateRouteSearch(update: Partial<AssetTreeSearch>) {
    if (onRouteSearchChange) onRouteSearchChange(update);
    else setLocalRouteSearch((previous) => ({ ...previous, ...update }));
  }
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const rowsById = new Map(allRows.map((row) => [row.id, row]));
  const assetPath: AssetViewRow[] = [];
  let pathNode = selected;
  while (pathNode) {
    assetPath.unshift(pathNode);
    pathNode =
      pathNode.parentId === null
        ? null
        : (rowsById.get(pathNode.parentId) ?? null);
  }
  // "All Assets" is a synthetic leading crumb representing the true root
  // scope (every root-level sibling) — not a real, selectable Asset node,
  // since the facility/Site itself is never a synthetic Work.Asset row.
  const breadcrumbPath: { id: number | null; name: string }[] = [
    { id: null, name: "All Assets" },
    ...assetPath,
  ];

  function rowMatchesKindAlone(row: AssetViewRow) {
    return kindFilter === "all" || row.kind === kindFilter;
  }

  function matchesActiveFilters(row: AssetViewRow) {
    const matchesSearch =
      normalizedSearch === "" ||
      row.name.toLocaleLowerCase().includes(normalizedSearch) ||
      row.code.toLocaleLowerCase().includes(normalizedSearch);
    return rowMatchesKindAlone(row) && matchesSearch;
  }

  const sort = resolveAssetSort(currentRouteSearch);
  const tableRows = sortAssetRows(
    allRows.filter((row) => {
      // Filtered browsing is a flat list of matching records across the
      // hierarchy (IWA-19835); clearing filters restores the drilldown.
      const matchesScope =
        normalizedSearch || kindFilter !== "all"
          ? true
          : row.parentId === effectiveSelectedId;
      if (!matchesScope) return false;
      return matchesActiveFilters(row);
    }),
    sort,
  );

  const kindTabs: { count: number; label: string; value: KindFilter }[] = [
    { count: allRows.length, label: "All", value: "all" },
    {
      count: allRows.filter((row) => row.kind === "ASSET").length,
      label: "Equipment",
      value: "ASSET",
    },
    {
      count: allRows.filter((row) => row.kind === "LOCATION").length,
      label: "Location",
      value: "LOCATION",
    },
  ];

  function changeKindFilter(value: string) {
    const nextKind = value === "all" ? "all" : (value as KindFilter);
    setKindFilter(nextKind);
    updateRouteSearch({
      assetKind: nextKind === "all" ? undefined : nextKind,
    });
  }

  // A new column starts ascending; the active column flips direction.
  function toggleSort(field: AssetSortField) {
    updateRouteSearch({
      assetSort: field,
      assetSortDir:
        sort?.field === field && sort.dir === "asc" ? "desc" : "asc",
    });
  }

  function navigateToAsset(id: number | null) {
    setSearch("");
    setKindFilter("all");
    const clearedFilters = { assetSearch: undefined, assetKind: undefined };
    if (id === null) {
      // The synthetic "All Assets" breadcrumb entry: return to the true
      // root scope (every root-level sibling), not a specific node.
      setTransientSelectedId(null);
      updateRouteSearch({ ...clearedFilters, assetSelectedId: undefined });
      return;
    }
    const assetId = rowsById.get(id)?.assetId ?? undefined;
    setTransientSelectedId(assetId ? null : id);
    updateRouteSearch({ ...clearedFilters, assetSelectedId: assetId });
  }

  function navigateToParent() {
    if (selected === null) return;
    if (selected.parentId === null) {
      // `selected` is itself a root-level sibling — "Up" from here goes to
      // the true root scope (every root-level sibling), not another row.
      navigateToAsset(null);
      return;
    }
    const parentId = selected.parentId;
    navigateToAsset(parentId);
  }

  function openRecord(assetId: AssetId) {
    updateRouteSearch({ assetOpenId: assetId });
  }

  /**
   * Selecting a sub-asset in the side sheet both switches the panel to that
   * record and drills the main table into it (so the grid shows the
   * sub-asset's own children and the breadcrumbs update), matching the
   * POC's sibling-switching behavior rather than only swapping the panel.
   */
  function selectSubAsset(assetId: AssetId) {
    const row = allRows.find((candidate) => candidate.assetId === assetId);
    setTransientSelectedId(row?.assetId ? null : (row?.id ?? null));
    setSearch("");
    setKindFilter("all");
    updateRouteSearch({
      assetOpenId: assetId,
      assetSelectedId: assetId,
      assetSearch: undefined,
      assetKind: undefined,
    });
  }

  function openCreatedWorkOrder(workOrderId: WorkOrderId) {
    if (!selectedCustomer || !selectedFacility) return;
    void navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
      search: {
        ...getWorkScopeSearch(selectedCustomer, selectedFacility),
        workOrderFullPage: true,
      },
    });
  }

  function closeRecord() {
    updateRouteSearch({ assetOpenId: undefined });
  }

  const assetDetailSearch = {
    ...(selectedCustomer && selectedFacility
      ? getWorkScopeSearch(selectedCustomer, selectedFacility)
      : {}),
    ...currentRouteSearch,
  };

  return (
    <AppShellContent pending={assetTreeQuery.isPending}>
      <WorkPage
        header={
          <Headline as="h1" size="lg">
            Assets
          </Headline>
        }
        toolbar={
          <SearchFilterToolbar
            onSearchChange={(value) => {
              setSearch(value);
              updateRouteSearch({ assetSearch: value || undefined });
            }}
            searchAriaLabel="Search assets"
            searchPlaceholder="Search assets..."
            searchValue={search}
          >
            <AssetKindFilter
              onChange={changeKindFilter}
              tabs={kindTabs}
              value={kindFilter}
            />
          </SearchFilterToolbar>
        }
      >
        {assetTreeQuery.isError ? (
          <WorkPageState tone="negative">
            <Text tone="negative">Unable to load assets.</Text>
          </WorkPageState>
        ) : (
          <WorkPanel>
            <div className="flex shrink-0 items-center justify-between border-b border-outline-subtle px-(--spacing-16) py-(--spacing-6)">
              <AssetBreadcrumbs
                onNavigate={navigateToAsset}
                path={breadcrumbPath}
              />
              <div className="flex shrink-0 items-center gap-(--spacing-12)">
                <Text size="sm" tone="subtle">
                  {tableRows.length} {tableRows.length === 1 ? "item" : "items"}
                </Text>
                {selected !== null ? (
                  <Button
                    onClick={navigateToParent}
                    size="sm"
                    variant="outline"
                  >
                    <Icon name="chevron-up" size="xs" tone="inherit" />
                    UP
                  </Button>
                ) : null}
              </div>
            </div>
            <AssetsTable
              allRows={allRows}
              emptyMessage={
                normalizedSearch
                  ? "No assets match your search."
                  : "This asset has no sub-assets."
              }
              onNavigate={navigateToAsset}
              onSelectRecord={openRecord}
              onSortChange={toggleSort}
              rows={tableRows}
              sort={sort}
            />
          </WorkPanel>
        )}
      </WorkPage>
      {assetTreeQuery.isError ? null : (
        <AssetDetailSideSheet
          assetCode={openRow?.code}
          assetDetailSearch={assetDetailSearch}
          assetId={openAssetId}
          assetKind={openRow?.kind}
          assetName={openRow?.name}
          onOpenChange={(open) => {
            if (!open) closeRecord();
          }}
          onStartWorkOrder={
            canCreateWorkOrder ? () => setIsNewWorkOrderOpen(true) : undefined
          }
          onSelectSubAsset={selectSubAsset}
          physicalLocation={
            physicalLocationRow
              ? {
                  name: physicalLocationRow.name,
                  code: physicalLocationRow.code,
                }
              : null
          }
          subAssets={openRowSubAssets}
        />
      )}
      <AssetWorkOrderModal
        asset={
          openRow
            ? {
                id: openRow.id,
                assetId: openRow.assetId,
                name: openRow.name,
                locationName: physicalLocationRow?.name ?? openRow.locationCode,
              }
            : null
        }
        onAttachProcedure={attachProcedure}
        onCreate={createWorkOrder}
        onCreated={(workOrderId) => {
          openCreatedWorkOrder(workOrderId);
        }}
        onOpenChange={setIsNewWorkOrderOpen}
        open={isNewWorkOrderOpen}
      />
    </AppShellContent>
  );
}
